import React, { useState, useEffect } from 'react';
import { TrendingUp, Wheat, Droplets, Activity, RefreshCw } from 'lucide-react';
import Card from '../components/ui/Card';
import ApiService from '../services/apiService';
import DiagnosticsPanel from '../components/DiagnosticsPanel';
import GlobalContextBar from '../components/GlobalContextBar';
import TranslationService from '../services/translationService';
import ValidatedInput from '../components/ui/ValidatedInput';
import { commitModule } from '../utils/reportMarkers';
import { readSSE } from '../utils/sseStream';
import { parseYieldRange } from '../utils/aiParsers';

// The LLM answers in kg/acre; the report's canonical unit is kg/hectare.
const ACRE_TO_HECTARE = 2.4710538;

const YieldPredictionView = ({ t, userId, isRTL, setReportData, selectedDevice }) => {
  // 1. Initialize State with defaults
  const [formData, setFormData] = useState({
    Crop: "rice",        // Default Crop
    Rainfall: 1200,      // Default Rainfall
    N: 0, 
    P: 0, 
    K: 0,
    Temperature: 25,
    Humidity: 50,
    pH: 6.5
  });
  
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [globalContext, setGlobalContext] = useState({});

  const [streamContent, setStreamContent] = useState("");
  const [thinkingContent, setThinkingContent] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Instant Client-Side Translation for language toggles
  useEffect(() => {
    if (isComplete && streamContent && !isStreaming) {
        const lang = isRTL ? 'ar' : 'en';
        setIsStreaming(true); // show brief shimmering animation
        TranslationService.translateText(streamContent, lang)
            .then(translated => {
                setStreamContent(translated);
            })
            .catch(err => console.error("Translation error", err))
            .finally(() => setIsStreaming(false));
            
        // translate thinking block as well
        if (thinkingContent) {
            TranslationService.translateText(thinkingContent, lang)
                .then(translated => setThinkingContent(translated))
                .catch(err => console.error(err));
        }
    }
  }, [isRTL]);

  // 2. Robust Change Handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      // Create a copy of the previous state
      const newState = { ...prev };

      // Update specific field based on type
      if (name === 'Crop') {
        newState[name] = value; // Keep as string
      } else if (name === 'Rainfall') {
        newState[name] = parseInt(value, 10); // Force Integer
      } else {
        newState[name] = parseFloat(value); // Float for sensors
      }

      return newState;
    });
  };

  // 3. Auto-Fill Helper
  const handleAutoFill = async () => {
    if (!selectedDevice) {
        alert(isRTL ? "يرجى تحديد جهاز استشعار من لوحة القيادة أولاً" : "Please select an IoT device from the Dashboard first");
        return;
    }
    try {
      const res = await ApiService.get(`/iot-history/${selectedDevice.id}?limit=1`);
      if (res.status === 'success' && res.data && res.data.length > 0) {
        const s = res.data[0];
        setFormData(prev => ({
          ...prev,
          N: Math.round(s.nitrogen || 0),
          P: Math.round(s.phosphorus || 0),
          K: Math.round(s.potassium || 0),
          Temperature: Math.round(s.temperature || 25),
          Humidity: Math.round(s.humidity || 50),
          pH: parseFloat((s.ph || 6.5).toFixed(1))
          // NOTE: We do not overwrite Crop or Rainfall here
        }));
      } else {
        alert(isRTL ? "لا توجد بيانات قراءة لهذا الجهاز" : "No live data available for this device");
      }
    } catch (e) {
      alert("Error fetching sensor data");
    }
  };

  // 4. Prediction Handler
  const handlePredict = async () => {
    setLoading(true);
    setPrediction(null);
    setIsStreaming(true);
    setIsThinking(true);
    setStreamContent("");
    setThinkingContent("");
    setIsComplete(false);
    setHasError(false);
    
    /* Declared outside the try so the catch can still commit a failed run.
       The report contract freezes these eight keys - do not add to them. */
    const yieldInputs = {
      Crop: globalContext.cropType || formData.Crop || "Unknown",
      Rainfall: parseInt(globalContext.waterQuantity || formData.Rainfall || 100),
      N: parseInt(formData.N),
      P: parseInt(formData.P),
      K: parseInt(formData.K),
      Temperature: parseInt(formData.Temperature),
      Humidity: parseInt(formData.Humidity),
      pH: parseFloat(formData.pH)
    };

    /* Local accumulator: the `streamContent` state value is a stale closure in here. */
    let fullText = "";
    let streamError = null;

    try {
      const payload = {
        user_id: userId,
        ...yieldInputs,
        lang: isRTL ? 'ar' : 'en',
        stream: true,
        context: globalContext
      };

      window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));

      const response = await fetch(`${ApiService.BASE_URL}/predict-yield`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
          },
          body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to connect to AI engine");

      /* Buffered SSE read - a raw chunk split drops the partial event that straddles
         every TCP boundary, which silently ate tokens out of the middle of the answer. */
      await readSSE(response, (evt) => {
          if (evt.error) {
              streamError = evt.error;
              setHasError(true);
              setErrorMessage(evt.error);
              return;
          }
          if (evt.reasoning) {
              setThinkingContent(prev => prev + evt.reasoning);
              return;
          }
          if (evt.content) {
              // First visible token means the model is past its scratch-pad.
              setIsThinking(false);
              fullText += evt.content;
              setStreamContent(prev => prev + evt.content);
          }
      });

      setIsComplete(true);
      setIsThinking(false);

      /* Report state: derive the structured prediction from the accumulated markdown
         and convert the model's kg/acre answer to the contract's kg/hectare. */
      const range = parseYieldRange(fullText);
      /* Convert ONLY when the model actually answered in kg/acre. The prompt
         asks for acre, but models do answer in kg/ha, and multiplying one of
         those by 2.47 put a yield 2.47x too high on the customer's report.
         A bare "kg" states no area unit; keep the prompt's assumption there,
         but record what was really seen in source_unit. */
      const srcUnit = range ? range.unit : null;
      const factor = srcUnit === 'kg/ha' ? 1 : ACRE_TO_HECTARE;
      const yieldPrediction = range
        ? {
            predicted_yield_kg_per_ha: Math.round(((range.low + range.high) / 2) * factor),
            unit: 'kg/ha',
            range_low_kg_per_ha: Math.round(range.low * factor),
            range_high_kg_per_ha: Math.round(range.high * factor),
            source_unit: srcUnit,
            source_low: range.low,
            source_high: range.high,
            parsed: true
          }
        : { predicted_yield_kg_per_ha: null, unit: 'kg/ha', parsed: false };

      commitModule(setReportData, 'yield', {
        yield_inputs: yieldInputs,
        yield_prediction: yieldPrediction,
        yield_ai_text: fullText
      }, !streamError, streamError ? String(streamError).slice(0, 200) : null);

    } catch (e) {
      console.error(e);
      setHasError(true);
      setErrorMessage(e.message);
      // A run that failed must stay distinguishable from a module that never ran.
      commitModule(setReportData, 'yield', {
        yield_inputs: yieldInputs,
        yield_prediction: { predicted_yield_kg_per_ha: null, unit: 'kg/ha', parsed: false },
        yield_ai_text: fullText
      }, false, String(e.message || e).slice(0, 200));
    } finally {
      setLoading(false);
      setIsStreaming(false);
    }
  };

return (
    <div className="h-full flex flex-col max-w-7xl mx-auto font-sans text-[#1B263B] dark:text-white pb-10 space-y-6">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full mb-2">
        <div>
           <h2 className="text-3xl font-bold flex items-center gap-3 text-[#1B263B] dark:text-white">
             <TrendingUp className="text-[#6B8F71] flex-shrink-0" size={32} /> {t.yield.title || "Yield Prediction Model"}
           </h2>
           <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t.yield.yieldSubtitle || "Estimate total crop harvest volume (kg/acre) utilizing environmental ML analytics."}</p>
        </div>
      </div>
      
      <div className="mb-4">
        <GlobalContextBar t={t} isRTL={isRTL} onContextChange={setGlobalContext} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT: Inputs Form (2 columns wide) */}
        <div className="lg:col-span-2 space-y-6 flex flex-col">

          {/* Section 2: Environmental Data (Sensors) */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm flex-1 flex flex-col transition-colors">
            <div className="flex justify-between items-center mb-5">
               <h3 className="font-bold text-lg text-[#1B263B] dark:text-white flex items-center gap-2">
                  <Activity size={20} className="text-[#6B8F71]"/> {t.yield.env || "Soil & Environmental Telemetry"}
               </h3>
               <button onClick={handleAutoFill} className="text-xs bg-[#6B8F71]/10 hover:bg-[#6B8F71]/20 text-[#6B8F71] px-3 py-1.5 rounded-full flex items-center gap-2 transition-all font-bold">
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {t.yield.autoFill || "Sync IoT Data"}
               </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-8">
              {[
                { label: t.yield.inputs.n || "Nitrogen (N)", name: "N" },
                { label: t.yield.inputs.p || "Phosphorus (P)", name: "P" },
                { label: t.yield.inputs.k || "Potassium (K)", name: "K" },
                { label: t.yield.inputs.temp || "Temperature °C", name: "Temperature" },
                { label: t.yield.inputs.hum || "Humidity %", name: "Humidity" },
                { label: t.yield.inputs.ph || "Soil pH", name: "pH", step: "0.1" },
              ].map((field) => (
                <div key={field.name} className="flex flex-col">
                  <ValidatedInput 
                    label={field.label}
                    type="number" 
                    name={field.name === 'Temperature' ? 'temperature' : field.name === 'Humidity' ? 'humidity' : field.name === 'Rainfall' ? 'rainfall' : field.name}
                    value={formData[field.name]} 
                    onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                    step="1"
                    lang={isRTL ? 'ar' : 'en'}
                  />
                </div>
              ))}
            </div>

            <button 
              onClick={handlePredict}
              disabled={loading}
              className="w-full mt-auto bg-[#C47A2C] hover:bg-[#A36322] text-white font-bold py-4 rounded-xl shadow-[max(0px,inset_0_-2px_4px_rgba(0,0,0,0.1))] transition-transform transform hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2 text-lg"
            >
              {loading ? <Activity size={24} className="animate-spin" /> : <TrendingUp size={24} />}
              {loading ? (t.yield.predictBtn || "Computing") + "..." : (t.yield.predictBtn || "Calculate Prediction")}
            </button>
          </div>
        </div>

        {/* RIGHT: Result Display (1 column wide) */}
        <div className="lg:col-span-1">
          <div className={`h-full flex flex-col items-center justify-center transition-all duration-500 relative overflow-hidden rounded-2xl shadow-sm border bg-white dark:bg-slate-800 border-[#1B263B]/5 dark:border-slate-700 min-h-[400px]`}>
             
             {/* Background Decoration */}
             <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
                <Wheat size={300} className={`absolute -bottom-10 ${isRTL ? '-left-10' : '-right-10'} text-[#1B263B] dark:text-white rotate-12`}/>
             </div>

             {/* Copper Laser Effect */}
             {loading && (
                 <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden">
                     <div className="absolute top-0 left-0 w-full h-1 bg-[#C47A2C] shadow-[0_0_15px_rgba(196,122,44,0.8)] animate-scan-laser"></div>
                     <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] bg-white/20 dark:bg-slate-900/20"></div>
                 </div>
             )}

             {(!isStreaming && !isComplete && !hasError && !loading) ? (
               <div className="text-center opacity-50 z-10 px-8 flex flex-col items-center">
                 <div className="w-24 h-24 bg-gray-50 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-5 border border-gray-100 dark:border-slate-800 shadow-inner">
                   <TrendingUp size={40} className="text-gray-300 dark:text-gray-600" />
                 </div>
                 <h4 className="text-xl font-bold text-[#1B263B] dark:text-white mb-2">{t.yield.ready || "Awaiting Inputs"}</h4>
                 <p className="text-sm font-medium text-gray-400 dark:text-gray-500 leading-relaxed max-w-[200px]">{t.yield.readyDesc || "Provide field configuration and sensor data to generate a yield forecast."}</p>
               </div>
             ) : (
                <div className="w-full h-full z-10 p-2 overflow-auto">
                 <DiagnosticsPanel
                    isStreaming={isStreaming}
                    streamContent={streamContent}
                    thinkingContent={thinkingContent}
                    isThinking={isThinking}
                    isComplete={isComplete}
                    hasError={hasError}
                    errorMessage={errorMessage}
                    onRetry={handlePredict}
                    onNewAnalysis={() => { setStreamContent(''); setThinkingContent(''); setIsComplete(false); }}
                    t={t}
                    isRTL={isRTL}
                 />
                </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YieldPredictionView;
